package com.siriusxm.pia.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.TableBuilder.ColumnBuilder
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Text

object SplitSelectionViewStyles : StyleSheet() {
    val container by style {
        display(DisplayStyle.Flex)
        gap(20.px)

        child(self, type("div")) style {
        }

        "> div:nth-child(1):nth-last-child(2)" style {
            width(250.px)
            flexShrink(0)
        }

        "> div:nth-child(1):nth-last-child(2) ~ div" {
            flex(1)
//            overflow("hidden")
        }
    }

    val selectionList by style {
        display(DisplayStyle.None)
        width(100.percent)
        whenWiderThanPhone {
            display(DisplayStyle.Block)
        }
    }

    val itemPopup by style {
        width(100.percent)
        display(DisplayStyle.Block)
        padding(0.px, 5.px, 5.px)
        whenWiderThanPhone {
            display(DisplayStyle.None)
        }
        type("select") style {
            width(100.percent)
        }
    }

    val itemMain by style {
        padding(8.px)
        display(DisplayStyle.Flex)
        gap(5.px)
        cursor("pointer")

        child(self, type("div")) style {
            display(DisplayStyle.InlineBlock)
        }

        type("input") style {
            property("height", "inherit")
            property("vertical-align", "-2px")
            cursor("pointer")
        }
    }

    val itemMainSelected by style {
        fontWeight("bold")
        backgroundColor(SXMUI.selectionBackgroundColor.value())
        color(SXMUI.selectionColor.value())
    }
}

class SplitSelectionViewProperties<T> {
    var title: String? = null
    var items: List<T>? = null
    var selection: T? = null

    /**
     * When true, the split view fills its parent container height exactly.
     * The left panel list becomes scrollable within its box, and the right
     * panel scrolls independently. Use in conjunction with
     * ServiceContentViewConfiguration.fixedHeight for full-height layouts.
     */
    var fixedHeight: Boolean = false
    internal var render: (@Composable (T) -> Unit)? = null
    internal var listItemRender: ((T) -> String) = {
        it.toString()
    }
    internal var onSelectCb: ((T) -> Unit)? = null
    internal val extraColumns = arrayListOf<ColumnBuilder<T>.() -> Unit>()

    internal val actions = mutableListOf<ButtonConfiguration>()

    fun render(block: @Composable (T) -> Unit) {
        this.render = block
    }

    fun renderListItem(block: (T) -> String) {
        this.listItemRender = block
    }

    fun onSelect(block: (T) -> Unit) {
        onSelectCb = block
    }

    fun column(columnBuilder: ColumnBuilder<T>.() -> Unit) {
        extraColumns += columnBuilder
    }

    fun action(title: String, config: ButtonConfiguration.() -> Unit) {
        actions += ButtonConfiguration(title).apply(config)
    }
}

@Composable
fun <T : Any> splitSelectionView(props: SplitSelectionViewProperties<T>) {
    var selection by mutableStateOf(props.selection)

    if (props.items != null) {
        Div({
            classes(SplitSelectionViewStyles.itemPopup)
        }) {
            val options: List<Pair<String, T>> = props.items!!.map {
                props.listItemRender(it) to it
            }
            simpleSelectNullable<T>(props.selection, options) { item ->
                if (props.onSelectCb != null) {
                    if (item != null) {
                        props.onSelectCb?.invoke(item)
                    }
                } else {
                    selection = item
                }
            }
        }

        Div({
            classes(SplitSelectionViewStyles.container)
            if (props.fixedHeight) {
                style {
                    height(100.percent)
                    minHeight(0.px)
                }
            }
        }) {

            Div({
                classes(SplitSelectionViewStyles.selectionList)
                if (props.fixedHeight) {
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        minHeight(0.px)
                    }
                }
            }) {
                box({
                    paddedContent = false
                    title = props.title
                    if (props.fixedHeight) {
                        scrollable = true
                    }

                    if (selection == null) {
                        props.actions.forEach {
                            this.action(it)
                        }
                    }
                }) {

                    table<T> {
                        items(props.items)

                        column {
                            style {
                                padding(0.px)
                            }
                            content { item ->
                                Div({
                                    classes(SplitSelectionViewStyles.itemMain)
                                    if (item == props.selection) {
                                        classes(SplitSelectionViewStyles.itemMainSelected)
                                    }
                                    onClick {
                                        if (props.onSelectCb != null) {
                                            props.onSelectCb?.invoke(item)
                                        } else {
                                            selection = item
                                        }
                                    }
                                }) {
                                    Div {
                                        Input(type = InputType.Radio) {
                                            this.checked(item == props.selection)
                                        }
                                    }

                                    Div {
                                        Text(props.listItemRender(item))

                                    }
                                }
                            }
                        }

                        if (selection == null) {
                            props.extraColumns.forEach {
                                column(null, it)
                            }
                        }
                    }
                }
            }
            if (selection != null) {
                Div({
                    style {
                        flex(1, 1)
                        if (props.fixedHeight) {
                            minHeight(0.px)
                            overflow("hidden")
                        }
                    }
                }) {
                    Div({
                        style {
                            if (props.fixedHeight) {
                                height(100.percent)
                                overflow("auto")
                                padding(8.px)
                            }
                        }
                    }) {
                        selection?.let {
                            props.render?.invoke(it)
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun <T : Any> splitSelectionView(config: SplitSelectionViewProperties<T>.() -> Unit) {
    splitSelectionView(SplitSelectionViewProperties<T>().apply(config))
}